import { PaintColor } from "@omni/types/api/paintColors/bff/paintColor.model";
import { PaintFamily } from "@omni/types/api/paintColors/bff/paintFamily.model";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import {
    blendColorsLinear,
    sortColorsByLuminance
} from "@omni/utils/color.helper";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useMemo } from "react";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { usePaintConfiguratorContext } from "@/components/PaintConfigurator/context/paintConfigurator.context";

type ColorsContextValue = {
    loadingFamilies: boolean;
    families: Array<{ id: string; name: string; hex: string }>;
    selectedFamily: PaintFamily | null;
    setSelectedFamily: (family: string | undefined) => void;

    loadingPaintColors: boolean;
    colors: Array<PaintColor>;
    selectedColor: PaintColor | null;
    setSelectedColor: (color: PaintColor) => void;
};

type ColorsContextProviderProps = RequireChildrenFor<{}>;

export const ColorsContext = createContext<ColorsContextValue | undefined>(
    undefined
);
ColorsContext.displayName = "ColorsContext";

export const ColorsContextProvider = ({
    children
}: ColorsContextProviderProps) => {
    const { state, setState } = usePaintConfiguratorContext();

    const [{ data: families = [], loading: loadingFamilies }] =
        useBFFProductServiceApi<any, any, Array<PaintFamily>>(
            {
                url: `/paint-colors/families`,
                params: {
                    collections: state.collection
                },
                method: "GET"
            },
            { autoCancel: false, manual: false, useCache: true }
        );

    const [{ data: colors = [], loading: loadingPaintColors }] =
        useBFFProductServiceApi<any, any, Array<PaintColor>>(
            {
                url: `/paint-colors/paints`,
                params: {
                    collections: state.collection,
                    families: state.family
                },
                method: "GET"
            },
            { autoCancel: false, manual: false, useCache: true }
        );

    const sortedColors = useMemo(() => {
        if (!isDefined(colors)) return [];

        return sortColorsByLuminance(colors, (c) => c.hexCode);
    }, [colors]);

    const onSelectFamily = (family: string | undefined) => {
        if (state.family === family || !isDefined(family)) {
            setState({ family: undefined });
            return;
        }
        setState({ family });
    };

    const onSelectColor = async (color: PaintColor) => {
        setState({
            colorHex: color.hexCode,
            colorId: color.id,
            colorName: color.name,
            family: color.family.id,
            collection: color.collection.id
        });
    };

    const enhancedFamilies = useMemo(() => {
        const mappedFamilies = (families ?? [])
            .map((family) => {
                const hex = blendColorsLinear(family.hexValues);
                if (!hex) return undefined;
                return {
                    id: family.id,
                    name: family.name,
                    hex
                };
            })
            .filter(isDefined);

        return sortColorsByLuminance(mappedFamilies, (f) => f.hex);
    }, [families]);

    const selectedFamily = useMemo(() => {
        if (!state.family) return null;
        return families.find((f) => f.id === state.family) || null;
    }, [families, state.family]);

    const selectedColor = useMemo(() => {
        if (!state.colorId) return null;
        return colors.find((c) => c.id === state.colorId) || null;
    }, [colors, state.colorId]);

    return (
        <ColorsContext
            value={{
                loadingFamilies,
                families: enhancedFamilies,
                selectedFamily,
                setSelectedFamily: onSelectFamily,

                loadingPaintColors,
                colors: sortedColors,
                selectedColor,
                setSelectedColor: onSelectColor
            }}
        >
            {children}
        </ColorsContext>
    );
};

export const useColorsContext = () => {
    const context = use<ColorsContextValue | undefined>(ColorsContext);

    if (!isDefined(context)) {
        throw new Error(`${ColorsContext.displayName} is undefined.`);
    }

    return context;
};
